import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  setRemoteBatteryLock,
  retireHardware,
  downloadPinCode,
  hardwareRemoteAction,
} from '../../api/hardware';
import {
  changeTab,
  getLocaleDateTimeString,
  history,
  rentable,
  toTitleCase,
  openGoogleMapsCoordinates,
  findAccess,
} from '../../utils';

import {
  UNASSIGNED,
  ACTIVE,
  INACTIVE,
  MAINTENANCE,
  RETIRED,
  RESERVED,
  REBALANCE,
  MISSING,
  LOST,
  STORED,
} from '../../constants';
import QRCode from '../../components/QRCode';
import HardwareAssignment from './newHardwareAssets/hardware-assignment';
import HardwareAssignmentsGraph from './newHardwareAssets/hardware-assignments-graph';
import SharedLocks from './newHardwareAssets/shared-locks';
import PageContainer from '../../components/Redesing/page-container';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import { PropertyList } from '../../components/Redesing/property-list';
import { PropertyListItem } from '../../components/Redesing/property-list-item';
import CardView from '../../components/Redesing/card-view';
import PageLoader from '../../components/Redesing/page-loader';
import Divider from '@mui/material/Divider';
import {
  LinearProgress,
  Skeleton,
  Box,
  Card,
  CardContent,
  CardHeader,
} from '@mui/material';
import HardwareRentals from './newHardwareAssets/hardware-rentals';
import MaintenanceTickets from './newHardwareAssets/hardware-tickets';
import MaintenanceLogs from './newHardwareAssets/hardware-logs';
import EventLogs from './newHardwareAssets/hardware-event-logs';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import PowerIcon from '@mui/icons-material/Power';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import SplitButton from '../../components/Redesing/split-button';
import PageContent from '../../components/Redesing/page-content';
import MovaticCustomModal from '../../components/Modal/MovaticCustomModal';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import PropertyListItemLink from '../../components/Redesing/property-list-item-link';
import ChangeOrganizationToSystem from '../../hooks/system/hook/ChangeOrganizationToSystem';
import { getHardwareIconByName } from '../../util/utilsR';
import CustomMapComponentForDetailLocation from '../../components/Redesing/custom-map-component-for-detail-location';
import { Badge } from '@mui/material';
import { useHardwareDetailRelayQuery } from '../../hooks/hardware/query/V2/useHardwareDetailRelay';
import { useHardwareLogsRelayQuery } from '../../hooks/hardware/query/V2/useHardwareLogsRelay';
import { useHardwareUnlocksRelayQuery } from '../../hooks/hardware/query/V2/useHardwareUnlocksRelay';
import HardwareUnlocks from './newHardwareAssets/hardware-unlocks';
import { UnavailabilityAlert } from './UnavailabilityAlert';
import { buildUnlockRows, countFailedUnlockAttempts } from '../../util/unlockEvents';
import { mapGraphQLToIHardware } from '../../utils/hardwareMappers';
import { UsageProgressBar } from '../../components/Maintenance/UsageProgressBar';
import useOrganization from '../../hooks/use-organization';
import { useSystemRelay } from '../../hooks/system/query/V2/useSystemRelay';
import { ISystem } from '../../ts/interface/system.interface';

// Lazy-loaded to keep future heavy diagnostics-only dependencies (e.g. h3-js)
// out of the main chunk.
const HardwareDiagnostics = lazy(
  () => import('./newHardwareAssets/diagnostics/hardware-diagnostics')
);

interface HardwareDetailProps {
  match: {
    params: {
      hardwareId: string;
      currentTab?: string;
      type: string;
    };
  };
  auth: unknown;
  systemAccess: string[];
  movaticAccess: boolean;
  system: {
    hardware_type_gid: string;
    hardware_type?: string;
    [key: string]: unknown;
  };
  systemLoaded: boolean;
  modeInfo: unknown;
}

/**
 * Skeleton component for hardware detail page
 */
function HardwareDetailSkeleton() {
  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header skeleton */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={1}>
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="text" width={150} height={24} />
          </Stack>
          <Skeleton variant="rectangular" width={100} height={40} />
        </Stack>

        {/* Tab skeleton */}
        <Skeleton variant="rectangular" width="100%" height={48} />

        {/* Content skeleton */}
        <Grid container spacing={2}>
          <Grid xs={12} lg={5} md={5}>
            <Card>
              <CardHeader title={<Skeleton variant="text" width={150} />} />
              <CardContent>
                <Stack spacing={2}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Stack key={i} direction="row" justifyContent="space-between">
                      <Skeleton variant="text" width={100} />
                      <Skeleton variant="text" width={150} />
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} lg={7} md={7}>
            <Card>
              <CardHeader title={<Skeleton variant="text" width={150} />} />
              <CardContent>
                <Skeleton variant="rectangular" height={200} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}

function HardwareDetail(props: HardwareDetailProps) {
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [showPincodeModal, setShowPincodeModal] = useState(false);
  const [refetchInterval, setRefetchInterval] = useState(5000);
  const { hardwareId, currentTab = 'general' } = props.match.params;
  const [currentTabV, setCurrentTabV] = useState(currentTab);
  const [generalTabTitle, setGeneralTabTitle] = useState('');
  const [maintenanceTicketCount, setMaintenanceTicketCount] = useState(0);
  const [hardwareLocation, setHardwareLocation] = useState({
    locationId: null as string | null,
    lat: null as number | null,
    lng: null as number | null,
    marker: {
      current_rental: null as string | null,
      state: 0,
      battery_percent: null as number | null,
      markerType: null as string | null,
      systemAccess: props.systemAccess,
    },
  });

  // Fetch hardware detail with Relay (only poll on Details tab)
  const hardwareQuery = useHardwareDetailRelayQuery(hardwareId, {
    refetchInterval: currentTab === 'general' ? refetchInterval : 0,
    onError: () => {
      setRefetchInterval(0);
    },
  });

  // Fetch tab data. Rentals are fetched (and searched/paginated server-side)
  // inside the Rentals tab itself, so they are not sourced here.
  const logsQuery = useHardwareLogsRelayQuery(hardwareId, {
    enabled: currentTab === 'maintenance-logs',
  });

  // Map GraphQL data to legacy interface.
  // Maintenance tickets are fetched (and filtered server-side) inside the
  // Maintenance Tickets tab itself, so they are not sourced here.
  const hardware = hardwareQuery.data
    ? mapGraphQLToIHardware(hardwareQuery.data, undefined, undefined, logsQuery.data)
    : null;

  // Also keep typed GraphQL data for new fields
  const hardwareGQL = hardwareQuery.data;

  const organization = useOrganization();
  const organizationId = organization.id || '';
  const isOrgContext = Boolean(organizationId);

  // Source system + modeInfo from GraphQL, keyed off the hardware's own system
  // (works in both system and org views; replaces Redux state.system/state.mode).
  const { system: systemInfo, modeInfo: systemModeInfo } = useSystemRelay(
    hardwareGQL?.systemId ?? undefined,
    { enabled: Boolean(hardwareGQL?.systemId) }
  );

  useEffect(() => {
    if (currentTab !== currentTabV) {
      setCurrentTabV(currentTab);
    }
  }, [currentTab, currentTabV]);

  const handleTabsChange = useCallback(
    (_event: React.SyntheticEvent, value: string) => {
      changeTab(`${hardwareId}/${value}`, currentTab, 'hardware');
    },
    [hardwareId, currentTab]
  );

  const access =
    findAccess(
      isOrgContext ? organization.access || props.systemAccess : props.systemAccess
    ) ?? [];
  const hardwareAccess = access.includes('physical') || props.movaticAccess;
  const isRentable = rentable(
    hardwareGQL?.hardwareType?.id,
    systemInfo?.hardware_type_gid
  );
  const hasUnavailabilityReasons =
    Object.keys(hardwareGQL?.unavailabilityReasons ?? {}).length > 0;

  useEffect(() => {
    if (hardwareGQL) {
      setHardwareLocation({
        locationId: hardwareGQL.locationId ?? null,
        lat: hardwareGQL.lat ?? null,
        lng: hardwareGQL.lng ?? null,
        marker: {
          current_rental: hardwareGQL.activeRentalId ?? null,
          state: hardwareGQL.state ?? 0,
          battery_percent: hardwareGQL.batteryPercent,
          markerType: hardwareGQL.hardwareType?.name ?? null,
          systemAccess: hardwareLocation.marker.systemAccess,
        },
      });

      const typeName = hardwareGQL.hardwareType?.name ?? '';
      // Mirror the identifier logic used by the "Type" column on the hardware
      // list page (hardwareColumnsV2 -> identifierRowFormat).
      const systemType = systemInfo?.hardware_type;
      const primaryIdentifier = hardwareGQL.hardwareProduct?.primaryIdentifier;
      const identifierByType: Record<string, string | number | null | undefined> = {
        number: hardwareGQL.number,
        mac: hardwareGQL.mac,
        imei: hardwareGQL.imei,
        vendor_id: hardwareGQL.vendorId,
        vendor_serial: hardwareGQL.vendorSerial,
      };
      const primaryIdentifierValue = primaryIdentifier
        ? identifierByType[primaryIdentifier]
        : undefined;
      const identifier =
        typeName === systemType
          ? hardwareGQL.number
          : primaryIdentifierValue ??
            hardwareGQL.label ??
            hardwareGQL.id ??
            hardwareGQL.number;
      const title = `${toTitleCase(typeName)} ${identifier ?? ''}`.trim();
      setGeneralTabTitle(title);

      // Count of tickets that are still open (not canceled or completed),
      // computed server-side so it is not capped by any fetch limit.
      setMaintenanceTicketCount(hardwareGQL.openMaintenanceTickets ?? 0);
    }
  }, [hardwareGQL, hardwareLocation.marker.systemAccess, systemInfo]);

  // Unlock events only apply to hardware whose product can be unlocked
  // remotely or over BLE. They are fetched separately (and eagerly) so the
  // Unlocks tab badge is ready before the tab is visited, and so a backend
  // failure on the unlocks calculations cannot blank the whole hardware
  // payload.
  const productSupportsUnlock = Boolean(
    hardwareGQL?.hardwareProduct?.supportsRemoteUnlock ||
      hardwareGQL?.hardwareProduct?.supportsBleUnlock
  );

  // Diagnostics tab only applies to hardware whose product communicates over TCP.
  const hasDiagnostics = Boolean(hardwareGQL?.hardwareProduct?.hasTcpConnection);

  const unlocksQuery = useHardwareUnlocksRelayQuery(hardwareId, {
    enabled: productSupportsUnlock,
  });

  const unlockRows = useMemo(
    () => buildUnlockRows(unlocksQuery.data?.unlocks || []).reverse(),
    [unlocksQuery.data]
  );
  const failedUnlockCount = useMemo(
    () => countFailedUnlockAttempts(unlockRows),
    [unlockRows]
  );

  const tabsList = [
    { label: 'Details', value: 'general' },
    { label: 'Rentals', value: 'rentals' },
    {
      label: (
        <>
          <Badge
            data-id="maintenance-ticket-count"
            badgeContent={maintenanceTicketCount}
            color="error"
            sx={{ mr: maintenanceTicketCount > 99 ? 2 : 1.5 }}
          >
            Maintenance Tickets
          </Badge>
        </>
      ),
      value: 'maintenance-tickets',
    },
    { label: 'Maintenance Logs', value: 'maintenance-logs' },
    { label: 'Event Logs', value: 'event-logs' },
    ...(productSupportsUnlock
      ? [
          {
            label:
              failedUnlockCount > 0 ? (
                <Badge
                  data-id="failed-unlock-count"
                  badgeContent={failedUnlockCount}
                  color="error"
                  sx={{ mr: failedUnlockCount > 99 ? 2 : 1.5 }}
                >
                  Unlocks
                </Badge>
              ) : (
                'Unlocks'
              ),
            value: 'unlocks',
          },
        ]
      : []),
    ...(hasDiagnostics ? [{ label: 'Diagnostics', value: 'diagnostics' }] : []),
  ];

  useEffect(() => {
    setCurrentTabV(currentTab);
  }, [currentTab]);

  const sendActions = (
    action: string,
    successMessage: string,
    errorMessage: string,
    loadingMessage: string
  ) => {
    return CustomAsyncToast({
      promise: () => hardwareRemoteAction(hardwareGQL?.oldId, action),
      successMessage: () => successMessage,
      errorMessage: errorMessage,
      loadingMessage: loadingMessage,
    });
  };

  const getHardwareActions = () => {
    const actions: Array<{
      type: string;
      desc: string;
      icon?: React.ReactNode;
      disabled?: boolean;
      action: (() => void) | null;
    }> = [];
    const hardwareProduct = hardwareGQL?.hardwareProduct;
    const hardwareOnline = hardwareGQL?.isOnline;

    if (hardwareGQL && hardwareAccess) {
      if (
        hardwareProduct?.supportsRemoteUnlock &&
        hardwareGQL.hardwareType?.name !== 'base_station'
      ) {
        actions.push({
          type: 'remoteUnlock',
          desc: 'Unlock',
          icon: <LockOpenIcon />,
          disabled: !hardwareOnline,
          action: () =>
            sendActions(
              'unlock',
              'Unlock request sent successfully',
              'There was an issue while sending the unlock request, please try again!',
              'Sending unlock request...'
            ),
        });
      }

      if (hardwareProduct?.supportsRemoteUnlock) {
        actions.push({
          type: 'remoteLock',
          desc: 'Lock',
          icon: <LockIcon />,
          disabled: !hardwareOnline,
          action: () =>
            sendActions(
              'lock',
              'Lock request sent successfully',
              'There was an issue while sending the lock request, please try again!',
              'Sending lock request...'
            ),
        });
      }

      if (hardwareProduct?.supportsRemoteBatteryUnlock) {
        actions.push({
          type: 'remoteBatteryUnlock',
          desc: 'Disconnect',
          icon: <PowerOffIcon />,
          disabled: !hardwareOnline,
          action: () =>
            sendActions(
              'unlockbattery',
              'Battery unlock request sent successfully',
              'There was an issue while sending the battery unlock request, please try again!',
              'Sending battery unlock request...'
            ),
        });
      }

      if (hardwareProduct?.supportsRemoteBatteryLock) {
        actions.push({
          type: 'remoteBatteryLock',
          desc: 'Connect',
          icon: <PowerIcon />,
          disabled: !hardwareOnline,
          action: () => {
            CustomAsyncToast({
              promise: () => setRemoteBatteryLock(hardwareGQL?.oldId),
              successMessage: () => 'Battery lock request sent successfully',
              errorMessage:
                'There was an issue while sending the battery lock request, please try again!',
              loadingMessage: 'Sending battery lock request...',
            });
          },
        });
      }

      if (hardwareProduct?.supportsRemoteRing) {
        actions.push({
          type: 'remoteRing',
          desc: 'Ring',
          icon: <NotificationsActiveIcon />,
          disabled: !hardwareOnline,
          action: () =>
            sendActions(
              'ring',
              'Ring request sent successfully',
              'There was an issue while sending the ring request, please try again!',
              'Sending ring request...'
            ),
        });
      }

      if (hardwareProduct?.supportsRemoteLocationCheck) {
        actions.push({
          type: 'locationCheck',
          desc: 'Locate',
          icon: <LocationOnIcon />,
          disabled: !hardwareOnline,
          action: () =>
            sendActions(
              'locationcheck',
              'Location request sent successfully',
              'There was an issue while sending the location request, please try again!',
              'Sending location request...'
            ),
        });
      }
    }
    if (actions.length > 0) {
      actions.unshift({
        type: 'initial',
        desc: 'Actions',
        action: null,
      });
    }
    return actions;
  };

  const goToHardwareStr = (id: string) => {
    return `/hardware/${id}`;
  };

  const genPinCode = () => {
    CustomAsyncToast({
      promise: () => downloadPinCode(hardware),
      successMessage: () => 'Pincodes generated successfully',
      errorMessage: 'There was an issue while making new pincodes, please try again!',
      loadingMessage: 'Generating pincodes...',
    });
  };

  const button = () => {
    const keybox = false; // TODO
    const bts = [
      {
        startIcon: <EditIcon />,
        label: 'Edit',
        onClick: () => history.push(`/hardware/${hardwareGQL?.id}/edit`),
        disabled: !!hardwareGQL?.activeRentalId || !hardwareAccess,
      },
      {
        startIcon: <DeleteIcon />,
        label: 'Retire',
        onClick: () => setShowRetireModal(true),
        disabled:
          hardwareGQL?.state === RETIRED ||
          !!hardwareGQL?.activeRentalId ||
          !hardwareAccess,
      },
    ];
    if (keybox) {
      bts.push({
        startIcon: <AddIcon />,
        label: 'Generate Pincodes',
        onClick: () => setShowPincodeModal(true),
        disabled:
          hardwareGQL?.state === RETIRED ||
          !!hardwareGQL?.activeRentalId ||
          !hardwareAccess,
      });
    }
    return bts;
  };

  const getHardwareStatus = () => {
    const statusMap: Record<
      number,
      { text: string; color: 'error' | 'success' | 'info' | 'warning' }
    > = {
      [UNASSIGNED]: { text: 'Unassigned', color: 'error' },
      [ACTIVE]: { text: 'Active', color: 'success' },
      [INACTIVE]: { text: 'Inactive', color: 'error' },
      [STORED]: { text: 'Stored', color: 'info' },
      [MAINTENANCE]: { text: 'Maintenance', color: 'warning' },
      [RETIRED]: { text: 'Retired', color: 'error' },
      [RESERVED]: { text: 'Retired', color: 'warning' },
      [REBALANCE]: { text: 'Rebalance', color: 'info' },
      [MISSING]: { text: 'Missing', color: 'error' },
      [LOST]: { text: 'Lost', color: 'error' },
    };

    const status = statusMap[hardwareGQL?.state ?? 0];

    if (status) {
      return <SeverityPill color={status.color}>{status.text}</SeverityPill>;
    }

    return <SeverityPill color="error">Unknown</SeverityPill>;
  };

  const getLockStateString = () => {
    if (hardwareGQL?.isUnlocked === true) {
      return <SeverityPill color="info">Unlocked</SeverityPill>;
    }

    if (hardwareGQL?.isUnlocked === false) {
      return <SeverityPill color="info">Locked</SeverityPill>;
    }

    return <SeverityPill color="warning">Unknown</SeverityPill>;
  };

  const renderLink = (to: string, text: string) => (
    <Link onClick={() => history.push(to)}>
      <Typography color="text.secondary" variant="subtitle1">
        {text}
        <i id="table_item_carot" className="fa fa-caret-right" />
      </Typography>
    </Link>
  );

  const onClickLastLocation = useCallback(() => {
    openGoogleMapsCoordinates(hardwareGQL?.lat, hardwareGQL?.lng);
  }, [hardwareGQL?.lat, hardwareGQL?.lng]);

  const hasLastCoordinates =
    Boolean(hardwareLocation?.lat) && Boolean(hardwareLocation?.lng);

  const generalContent = () => {
    const batteryLevel = hardwareGQL?.batteryPercent;
    const batteryVoltage = hardwareGQL?.batteryVoltage;
    const batteryCharging = hardwareGQL?.charging;
    const hardwareProduct = hardwareGQL?.hardwareProduct;

    return (
      <PropertyList>
        {isOrgContext && (
          <>
            <ChangeOrganizationToSystem
              system={{
                id: systemInfo.id,
                gid: systemInfo.gid,
                name: systemInfo.name,
              }}
            />
            <Divider />
          </>
        )}
        {hardwareGQL?.number !== null && hardwareGQL?.number !== undefined && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label={`${toTitleCase(hardwareGQL?.hardwareType?.name ?? '')} Number`}
              value={hardwareGQL?.number}
              dataId="hardware-number"
            />
            <Divider />
          </>
        )}
        {hardwareGQL?.label && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="Label"
              value={hardwareGQL?.label}
              dataId="label"
            />
            <Divider />
          </>
        )}
        {hardwareGQL?.vendorId && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="Vendor ID"
              value={hardwareGQL?.vendorId}
              dataId="vendor-id"
            />
            <Divider />
          </>
        )}
        {hardwareGQL?.mac && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="Mac Address"
              value={hardwareGQL?.mac}
              dataId="mac-address"
            />
            <Divider />
          </>
        )}
        {hardwareGQL?.imei && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="IMEI"
              value={hardwareGQL?.imei}
              dataId="imei"
            />
            <Divider />
          </>
        )}
        {hardwareProduct?.name && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="Product Name"
              value={hardwareProduct?.name}
              dataId="product-name"
            />
            <Divider />
          </>
        )}
        {hardwareGQL?.hardwareProductVersion?.name && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="Product Version"
              value={hardwareGQL?.hardwareProductVersion?.name}
              dataId="version-name"
            />
            <Divider />
          </>
        )}
        {hardwareGQL?.iccid && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="ICCID"
              value={hardwareGQL?.iccid}
              dataId="iccid"
            />
            <Divider />
          </>
        )}
        {hardwareGQL?.firmwareVersion && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="Firmware Version"
              value={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <span>{hardwareGQL.firmwareVersion}</span>
                  {hardwareGQL.hasLatestFirmwareVersion === false && (
                    <Tooltip
                      title={`This hardware is not using the latest firmware version. It should be upgraded to ${
                        hardwareGQL.hardwareProduct?.latestFirmwareVersion ??
                        'the latest version'
                      }.`}
                      arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            bgcolor: '#fff8e1',
                            color: 'text.primary',
                            border: '1px solid #ffe082',
                            fontSize: '0.8125rem',
                            fontWeight: 400,
                            px: 1.5,
                            py: 1,
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                            maxWidth: 280,
                            '& .MuiTooltip-arrow': {
                              color: '#fff8e1',
                              '&::before': {
                                border: '1px solid #ffe082',
                              },
                            },
                          },
                        },
                      }}
                    >
                      <ErrorOutlineIcon color="warning" fontSize="small" />
                    </Tooltip>
                  )}
                </Stack>
              }
              dataId="firmware"
            />
            <Divider />
          </>
        )}

        {isRentable && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="Rental Availability"
              dataId="availability"
              value={
                <SeverityPill color={hardwareGQL?.isAvailable ? 'success' : 'warning'}>
                  {hardwareGQL?.isAvailable
                    ? 'Available'
                    : hardwareGQL?.activeRentalId
                    ? 'In Rental'
                    : 'Unavailable'}
                </SeverityPill>
              }
            />
            <Divider />
          </>
        )}

        <>
          <PropertyListItem
            align={'horizontal'}
            label="State"
            dataId="current-state"
            value={<div>{getHardwareStatus()}</div>}
          />
          <Divider />
        </>

        {hardwareGQL?.isOnline !== null && hardwareGQL?.isOnline !== undefined && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="Connection Status"
              value={
                hardwareGQL?.isOnline ? (
                  <SeverityPill color={'success'}>Online</SeverityPill>
                ) : (
                  <SeverityPill color={'error'}>Offline</SeverityPill>
                )
              }
              dataId="tcp-state"
            />
            <Divider />
          </>
        )}

        {hardwareProduct?.hasBatteryStatus && (
          <>
            {(!!batteryLevel || batteryLevel === 0) && (
              <>
                <PropertyListItem
                  align={'horizontal'}
                  label="Battery Level"
                  value={
                    <>
                      <LinearProgress
                        value={batteryLevel}
                        variant="determinate"
                        color={
                          batteryLevel >= 20
                            ? batteryLevel > 50
                              ? 'success'
                              : 'warning'
                            : 'error'
                        }
                        sx={{
                          height: 8,
                          width: 36,
                        }}
                      />
                      {batteryLevel}%
                    </>
                  }
                  dataId="battery-level"
                />
                <Divider />
              </>
            )}
            {(!!batteryVoltage || batteryVoltage === 0) && (
              <>
                <PropertyListItem
                  align={'horizontal'}
                  label="Battery Voltage"
                  value={`${batteryVoltage} mV`}
                  dataId="battery-voltage"
                />
                <Divider />
              </>
            )}
            <>
              <PropertyListItem
                align={'horizontal'}
                label="Battery Charging Status"
                value={
                  <SeverityPill color={'info'}>
                    {batteryCharging ? 'Charging' : 'Not Charging'}
                  </SeverityPill>
                }
                dataId="battery-charging"
              />
              <Divider />
            </>
          </>
        )}

        <PropertyListItem
          align={'horizontal'}
          label="Lock State"
          value={getLockStateString()}
          dataId="lock-state"
          divider
        />

        {hardwareGQL?.locationId && !hardwareGQL?.activeRentalId && (
          <>
            <PropertyListItem align={'horizontal'} label={'Assigned Location'}>
              {renderLink(
                `/locations/${hardwareGQL?.locationId}/general`,
                hardwareGQL?.location?.name ?? ''
              )}
            </PropertyListItem>
            <Divider />
          </>
        )}
        {hardwareGQL?.activeRentalId && (
          <>
            <PropertyListItem align={'horizontal'} label={'Current Rental'}>
              {renderLink(
                `/rentals/${hardwareGQL?.activeRentalId}`,
                hardwareGQL?.activeRentalId
              )}
            </PropertyListItem>
            <Divider />
          </>
        )}
        {hardwareGQL?.vendorSerial && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label="Serial Number"
              value={hardwareGQL?.vendorSerial}
              dataId="serial-number"
            />
            <Divider />
          </>
        )}

        {hardwareGQL?.parent && (
          <>
            <PropertyListItem
              align={'horizontal'}
              label={`Current ${toTitleCase(
                hardwareGQL.parent.hardwareType?.name ?? ''
              )}`}
              dataId={`parent-${hardwareGQL.parent.hardwareType?.name}`}
            >
              {renderLink(
                goToHardwareStr(hardwareGQL.parent.id),
                String(
                  hardwareGQL.parent.number ??
                    hardwareGQL.parent.label ??
                    hardwareGQL.parent.id
                )
              )}
            </PropertyListItem>
            <Divider />
          </>
        )}

        <div>
          <PropertyListItem
            align={'horizontal'}
            label="Last Update"
            value={
              hardwareGQL?.lastSeen
                ? getLocaleDateTimeString(hardwareGQL?.lastSeen)
                : 'Unknown'
            }
            dataId="last-update"
          />
        </div>

        {(hardwareProduct?.hasLocationTracking || hasLastCoordinates) && (
          <div>
            <Divider />
            <PropertyListItemLink
              text={
                hasLastCoordinates
                  ? `${hardwareGQL?.lat}, ${hardwareGQL?.lng}`
                  : 'Unknown'
              }
              label={'Last GPS Location'}
              onClick={onClickLastLocation}
            />
          </div>
        )}

        {hardwareGQL?.qrCode?.id && (
          <>
            <Divider />
            <PropertyListItem
              align={'horizontal'}
              label="Assigned QR Code"
              dataId="qr-code"
            >
              <div>
                <Typography
                  sx={{
                    maxWidth: '80%',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                    paddingRight: 2,
                  }}
                  color="text.secondary"
                  variant="subtitle1"
                >
                  {hardwareGQL.qrCode.id}
                </Typography>
                <QRCode
                  value={`qr.movatic.co/?${hardwareGQL?.qrCode.id}`}
                  size="150"
                  level="H"
                  fileName={`qr-hardware-${hardwareGQL?.id}.png`}
                />
              </div>
            </PropertyListItem>
          </>
        )}

        {hardwareGQL?.oldId && (
          <>
            <Divider />
            <PropertyListItem
              align={'horizontal'}
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <span>Default QR Code</span>
                  <Tooltip
                    title="This QR code was automatically generated to uniquely identify this unit. You can still assign an additional QR code number if available."
                    arrow
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      color="action"
                      sx={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                </Stack>
              }
              dataId="default-qr-code"
            >
              <div>
                <Typography
                  sx={{
                    maxWidth: '80%',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                    paddingRight: 2,
                  }}
                  color="text.secondary"
                  variant="subtitle1"
                >
                  {hardwareGQL.oldId}
                </Typography>
                <QRCode
                  value={`qr.movatic.co?${hardwareGQL.oldId}`}
                  size="150"
                  level="H"
                  fileName={`default-qr-hardware-${hardwareGQL?.id}.png`}
                />
              </div>
            </PropertyListItem>
          </>
        )}

        {hardwareGQL?.vendorQrCode && (
          <>
            <Divider />
            <PropertyListItem
              align={'horizontal'}
              label="Vendor QR Code"
              dataId="vendor-qr-code"
            >
              <div>
                <Typography
                  sx={{
                    maxWidth: '80%',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                    paddingRight: 2,
                  }}
                  color="text.secondary"
                  variant="subtitle1"
                >
                  {hardwareGQL?.vendorQrCode}
                </Typography>
                {!!hardwareGQL?.vendorQrCode && (
                  <QRCode
                    value={hardwareGQL?.vendorQrCode}
                    size="150"
                    level="H"
                    fileName={`vendor-qr-hardware-${hardwareGQL?.id}.png`}
                  />
                )}
              </div>
            </PropertyListItem>
          </>
        )}
      </PropertyList>
    );
  };

  const { Icon } = getHardwareIconByName(hardwareGQL?.hardwareType?.name ?? '');

  // Show loading skeleton while initial data loads or when navigating to different hardware
  const isLoadingNewHardware =
    hardwareQuery.isLoading ||
    !hardwareQuery.data ||
    hardwareQuery.data.id !== hardwareId;

  if (isLoadingNewHardware) {
    return <HardwareDetailSkeleton />;
  }

  return (
    <div>
      <PageContainer isLoading={false}>
        <>
          <PageContent
            warningBanner={
              hasUnavailabilityReasons ||
              hardwareGQL?.hasLatestFirmwareVersion === false ? (
                <Stack spacing={1}>
                  <UnavailabilityAlert
                    reasons={hardwareGQL?.unavailabilityReasons}
                    typeName={hardwareGQL?.hardwareType?.name}
                  />
                  {hardwareGQL?.hasLatestFirmwareVersion === false ? (
                    <Alert
                      severity="warning"
                      icon={<ErrorOutlineIcon fontSize="inherit" />}
                    >
                      This hardware is not using the latest firmware version. It should be
                      upgraded to{' '}
                      {hardwareGQL.hardwareProduct?.latestFirmwareVersion ??
                        'the latest version'}
                      .
                    </Alert>
                  ) : null}
                </Stack>
              ) : undefined
            }
            itemTopButtons={<IconButtonMenu buttons={button()} />}
            backLink={'/hardware'}
            contentTitle={'Hardware'}
            itemName={generalTabTitle}
            detailIcon={<Icon />}
            itemChipValue={hardwareGQL?.id}
            chipLabel={'Hardware ID'}
            itemActions={
              <>
                {getHardwareActions().length > 0 && (
                  <SplitButton items={getHardwareActions()} />
                )}
              </>
            }
            tabsChildren={
              <>
                {currentTab === 'general' && (
                  <Grid container spacing={2}>
                    <Grid xs={12} lg={5} md={5}>
                      <Stack spacing={2}>
                        <CardView content={generalContent()} title={'Hardware Details'} />
                      </Stack>
                    </Grid>

                    <Grid xs={12} lg={7} md={7}>
                      <Stack spacing={2}>
                        {hardwareGQL?.hardwareProduct?.hasLocationTracking &&
                          hasLastCoordinates && (
                            <CustomMapComponentForDetailLocation
                              hardwareInfo={hardwareLocation}
                              system={systemInfo as unknown as ISystem}
                              modeInfo={systemModeInfo}
                            />
                          )}
                        <HardwareAssignmentsGraph hardware={hardware as any} />
                        <HardwareAssignment hardware={hardware as any} />
                        {isRentable && hardwareGQL?.id && (
                          <SharedLocks
                            hardwareId={hardwareGQL.id}
                            systemId={hardwareGQL.systemId}
                            canEdit={hardwareAccess}
                          />
                        )}
                        {props.movaticAccess && hardwareGQL?.maintenanceUsageStatus && (
                          <Card>
                            <CardHeader title="Maintenance Usage" />
                            <CardContent>
                              <UsageProgressBar
                                current={hardwareGQL.maintenanceUsageStatus.current}
                                threshold={hardwareGQL.maintenanceUsageStatus.threshold}
                              />
                              {hardwareGQL.maintenanceUsageStatus.ticketId && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption">
                                    <Link
                                      onClick={() =>
                                        history.push(
                                          `/maintenance/${
                                            hardwareGQL.maintenanceUsageStatus!.ticketId
                                          }`
                                        )
                                      }
                                      sx={{ cursor: 'pointer' }}
                                    >
                                      Maintenance ticket #
                                      {hardwareGQL.maintenanceUsageStatus.ticketId}
                                    </Link>
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                )}
                {currentTab === 'rentals' && (
                  <HardwareRentals hardware={hardware as any} />
                )}
                {currentTab === 'maintenance-tickets' && (
                  <MaintenanceTickets hardware={hardware as any} />
                )}
                {currentTab === 'maintenance-logs' && (
                  <MaintenanceLogs
                    hardware={hardware as any}
                    isLoading={logsQuery.isLoading}
                  />
                )}
                {currentTab === 'event-logs' && (
                  <EventLogs
                    isLoading={hardwareQuery.isLoading}
                    hardware_id={hardwareGQL?.id}
                  />
                )}
                {currentTab === 'unlocks' && productSupportsUnlock && (
                  <HardwareUnlocks
                    rows={unlockRows}
                    successRate={unlocksQuery.data?.unlockSuccessRate ?? null}
                    isLoading={unlocksQuery.isLoading}
                    error={unlocksQuery.error}
                  />
                )}
                {currentTab === 'diagnostics' && hasDiagnostics && (
                  <Suspense fallback={<PageLoader minHeight="400px" />}>
                    <HardwareDiagnostics
                      hardwareId={hardwareGQL?.id ?? ''}
                      vendorId={hardwareGQL?.vendorId ?? null}
                      firmwareVersion={hardwareGQL?.firmwareVersion ?? null}
                      systemId={hardwareGQL?.systemId ?? null}
                    />
                  </Suspense>
                )}
              </>
            }
            handleTabsChange={handleTabsChange}
            currentTabV={currentTabV}
            tabsList={tabsList}
          />
          <div>
            <MovaticCustomModal
              open={showPincodeModal}
              onClose={() => setShowPincodeModal(false)}
              title={'Generate New Pincodes'}
              content="Are you sure you would like to generate pincodes? This will require you to reprogram your keybox."
              action={
                <IconButtonMenu
                  buttons={[
                    {
                      label: 'Cancel',
                      onClick: () => setShowPincodeModal(false),
                    },
                    {
                      label: 'Generate',
                      onClick: () => {
                        genPinCode();
                        setShowPincodeModal(false);
                      },
                    },
                  ]}
                />
              }
            />

            <MovaticCustomModal
              open={showRetireModal}
              onClose={() => setShowRetireModal(false)}
              title={`Retire ${hardwareGQL?.hardwareType?.name}`}
              content={`Are you sure you would like to retire this ${hardwareGQL?.hardwareType?.name}?`}
              action={
                <IconButtonMenu
                  buttons={[
                    {
                      label: 'Cancel',
                      onClick: () => setShowRetireModal(false),
                    },
                    {
                      label: 'Retire',
                      onClick: () => {
                        CustomAsyncToast({
                          promise: () => retireHardware(hardwareGQL?.oldId ?? ''),
                          successMessage: () => 'Hardware retired successfully',
                          errorMessage:
                            'There was an issue while retiring the hardware, please try again!',
                          loadingMessage: 'Retiring Hardware...',
                        }).then((r) => {
                          if (r) {
                            history.push('/hardware');
                          }
                        });
                      },
                    },
                  ]}
                />
              }
            />
          </div>
        </>
      </PageContainer>
    </div>
  );
}

export default connect(
  (state: any) => ({
    auth: state.admin.admin,
    systemAccess: state.admin.systemAccess,
    movaticAccess: state.admin.admin.admin.movaticAccess,
    hardware: state.hardware.new_hardware,
    hardwareTypes: state.hardware.hardwareTypes,
    system: state.system.current,
    baseStationUnlockId: state.hardware.baseStationUnlockId,
    remoteUnlockId: state.hardware.remoteUnlockId,
    remoteLockId: state.hardware.remoteLockId,
    remoteRingId: state.hardware.remoteRingId,
    locationCheckId: state.hardware.locationCheckId,
    remoteActionLoaded: state.hardware.remoteActionLoaded,
    systemLoaded: state.system.isLoaded,
    modeInfo: state.mode,
  }),
  () => ({})
)(HardwareDetail);
